import React, { useEffect } from 'react';
import {
  TableContainer,
  TablePagination,
  TableRow,
  Table,
  TableBody,
  TableCell,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import EnhancedTableHead from './EnhancedTableHead';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  paper: {
    width: '100%',
    table: {
      minWidth: 750,
    },
    visuallyHidden: {
      border: 0,
      clip: 'rect(0 0 0 0)',
      height: 1,
      margin: -1,
      overflow: 'hidden',
      top: 20,
      width: 1,
      padding: 0,
    },
    marginBottom: theme.spacing(2),
  },
  table: {
    minWidth: 750,
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    top: 20,
    width: 1,
    padding: 0,
  },
  position: 'absolute',
  avatar: {
    color: '#FFFFFF',
    backgroundColor: '#88BCDE',
    width: 32,
    height: 32,
    fontSize: 14,
  },
  addIcon: {
    color: 'transparent',
    padding: 0,
  },
  title: {
    fontWeight: 'bold',
    fontSize: 11,
    color: '#4D4D4D',
  },
  data: {
    fontSize: 12,
    color: '#000000',
    fontFamily: 'SFProDisplay !important',
  },
  pagination: {
    color: 'rgba(0, 0, 0, 0.5)',
    fontFamily: 'SFProDisplay !important',
  },
  row: {
    height: 32,
  },
  chip: {
    height: 20,
  },
}));

export default function EnhancedTable({
  getData,
  rows,
  headCells,
  count = 0,
  pagination = true,
  update,
  containerClass,
  resetSort,
  rowsPerPageOptions,
  nthStyle = true,
  fill = true,
  slice = false,
  tableWidth = {},
}) {
  const classes = useStyles();
  const [order, setOrder] = React.useState('asc');
  const [orderBy, setOrderBy] = React.useState('');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(
    rowsPerPageOptions ? rowsPerPageOptions[0] : 5,
  );
  useEffect(() => {
    setOrder('asc');
    setOrderBy('');
    setPage(0);
    setRowsPerPage(rowsPerPageOptions ? rowsPerPageOptions[0] : 5);
  }, [resetSort]);

  useEffect(() => {
    const orderList = orderBy.split('+');
    if (orderList.length === 1 && getData) {
      getData({
        page,
        size: rowsPerPage,
        sort: orderBy ? `${orderBy},${order}` : '',
      });
    } else if (getData) {
      getData({
        page,
        size: rowsPerPage,
        sort: `${orderList[0]},${order}&sort=${orderList[1]},${order}`,
      });
    }
  }, [page, rowsPerPage, order, orderBy, getData, update]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const emptyRows = rowsPerPage - Math.min(rowsPerPage, count - page * rowsPerPage);
  const renderedRows = !slice
    ? rows
    : rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <div className={classes.root}>
      <TableContainer className={containerClass || ''}>
        <Table className={classes.table} sx={tableWidth} size="small" stickyHeader>
          <EnhancedTableHead
            classes={classes}
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            rowCount={rows.length}
            headCells={headCells}
          />
          <TableBody>
            {renderedRows.map((row, index) => {
              const key = row.id ?? row.code ?? index;
              return (
                <TableRow
                  hover
                  tabIndex={-1}
                  key={key}
                  className={classes.row}
                  sx={index % 2 === 0 && nthStyle ? { backgroundColor: '#fff' } : undefined}
                  onClick={row.click}
                >
                  {headCells.map((headCell) => {
                    const value = row[headCell.id];
                    return (
                      <TableCell
                        size="small"
                        key={headCell.id}
                        align={headCell.align}
                        sx={headCell.rowStyle}
                      >
                        {headCell.render({ value, row, index })}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
            {fill && emptyRows > 0 && (
              <TableRow style={{ height: 53 * emptyRows }}>
                <TableCell colSpan={headCells.length} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {pagination && (
        <TablePagination
          labelRowsPerPage="Lignes par page"
          rowsPerPageOptions={rowsPerPageOptions || [5, 10, 25]}
          component="div"
          count={count}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          classes={{ caption: classes.pagination, select: classes.pagination }}
        />
      )}
    </div>
  );
}
